import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FadeUp from "./FadeUp";
import { TESTIMONIALS } from "../data/content";

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const count = TESTIMONIALS.length;

  const go = useCallback(
    (dir) => setIndex((i) => (i + dir + count) % count),
    [count],
  );

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 5000);
    return () => clearInterval(id);
  }, [count]);

  const t = TESTIMONIALS[index];

  return (
    <section id="testimonials" className="bg-[#f4f9fb] py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
        {/* Left illustration */}
        <FadeUp>
          <div className="relative mx-auto w-fit">
            <div className="h-72 w-72 overflow-hidden rounded-full bg-brand-tint shadow-cardHover sm:h-80 sm:w-80">
              <img
                src="https://picsum.photos/seed/jsr-happy-client/500/500"
                alt="Happy JSR Travels client"
                className="h-full w-full object-cover"
              />
            </div>
            <div className="absolute -left-2 top-10 grid h-14 w-14 animate-float place-items-center rounded-full bg-white text-2xl shadow-cardHover">
              💬
            </div>
            <div className="absolute -right-2 bottom-12 grid h-12 w-12 animate-float place-items-center rounded-full bg-accent text-xl text-white shadow-cardHover [animation-delay:1s]">
              ★
            </div>
          </div>
        </FadeUp>

        {/* Right content */}
        <div>
          <FadeUp>
            <p className="section-label">← Testimonials →</p>
            <h2 className="heading-xl text-3xl sm:text-4xl">
              Our Clients <span className="text-brand">Feedback</span> Here
            </h2>
          </FadeUp>

          <div className="relative mt-6 min-h-[180px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.4 }}
              >
                <p className="text-amber-500">★★★★★</p>
                <blockquote className="mt-3 text-lg italic leading-relaxed text-navy/75">
                  “{t.quote}”
                </blockquote>
                <p className="mt-4 font-heading font-bold text-navy">
                  {t.name}
                </p>
                <p className="text-sm text-accent">{t.role}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Avatars + arrows */}
          <div className="mt-8 flex items-center justify-between">
            <div className="flex -space-x-3">
              {TESTIMONIALS.map((tt, i) => (
                <button
                  key={tt.name}
                  onClick={() => setIndex(i)}
                  aria-label={`Show ${tt.name}'s review`}
                  className={`h-12 w-12 overflow-hidden rounded-full border-2 transition-all ${
                    i === index ? "border-accent" : "border-white"
                  }`}
                >
                  <img
                    src={`https://picsum.photos/seed/jsr-avatar-${i}/80/80`}
                    alt={tt.name}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => go(-1)}
                aria-label="Previous testimonial"
                className="grid h-11 w-11 place-items-center rounded-full border border-navy/15 text-navy transition-colors hover:bg-brand hover:text-white"
              >
                ←
              </button>
              <button
                onClick={() => go(1)}
                aria-label="Next testimonial"
                className="grid h-11 w-11 place-items-center rounded-full bg-brand text-white transition-colors hover:bg-brand-dark"
              >
                →
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
